const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.ogv', '.mov', '.m4v', '.3gp', '.3g2']

const VIDEO_ACCEPT = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/ogv',
  'video/mov',
  'video/m4v',
  'video/3gp',
  'video/3g2'
].join(',')

const VIDEO_WIDTHS = ['auto', '250px', '400px']
const MAX_ANNOUNCEMENT_VIDEO_DURATION = 120
const MAX_ANNOUNCEMENT_VIDEO_TOTAL_SIZE = 500 * 1024 * 1024

function isSupportedVideoFile(file) {
  const name = String((file && file.name) || '').toLowerCase()
  return VIDEO_EXTENSIONS.some((extension) => name.endsWith(extension))
}

function isValidVideoDuration(duration) {
  return Number.isFinite(duration) && duration > 0 && duration <= MAX_ANNOUNCEMENT_VIDEO_DURATION
}

function normalizeVideoWidth(width) {
  if (width === 'Original' || width === '100%') {
    return 'auto'
  }
  if (width === '250' || width === '400') {
    return `${width}px`
  }
  return VIDEO_WIDTHS.includes(width) ? width : 'auto'
}

function resolvePublicMediaUrl(path, baseUrl) {
  const value = String(path || '').trim()
  const base = String(baseUrl || '').trim()

  if (!value || !base || value.startsWith('//')) {
    throw new Error('A secure video URL is required')
  }

  const resolved = /^https:/i.test(value)
    ? new URL(value)
    : new URL(value.replace(/^\/+/, ''), base.endsWith('/') ? base : `${base}/`)

  if (resolved.protocol !== 'https:') {
    throw new Error('Only HTTPS video URLs can be saved')
  }

  return resolved.toString()
}

function buildOssUploadUrl(stsData) {
  const endpoint = String((stsData && stsData.endpoint) || '').replace(/\/+$/, '')
  const bucketName = String((stsData && stsData.bucketName) || '')
  const objectKey = String((stsData && stsData.objectKey) || '')

  if (!endpoint || !bucketName || !objectKey) {
    throw new Error('Incomplete video upload credentials')
  }

  const host = endpoint.replace(/^https?:\/\//i, '')
  const encodedObjectKey = objectKey
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/')

  return `https://${bucketName}.${host}/${encodedObjectKey}`
}

function buildOssCanonicalHeaders({ requestDate, securityToken }) {
  return [`x-oss-date:${requestDate}`, `x-oss-security-token:${securityToken}`]
    .sort()
    .join('\n')
    .concat('\n')
}

function buildOssStringToSign({ bucketName, objectKey, contentType, requestDate, securityToken }) {
  const canonicalHeaders = buildOssCanonicalHeaders({ requestDate, securityToken })
  const canonicalResource = `/${bucketName}/${objectKey}`

  return ['PUT', '', contentType, requestDate, canonicalHeaders + canonicalResource].join('\n')
}

function normalizeOssEtag(etag) {
  return String(etag || '').replace(/^"+|"+$/g, '')
}

function createCompletePayload(file, objectKey, etag) {
  return {
    objectKey,
    originalFileName: file.name,
    size: file.size,
    contentType: file.type || 'video/mp4',
    etag: normalizeOssEtag(etag)
  }
}

function containsTemporaryVideoSource(html) {
  return /<video\b[^>]*\bsrc\s*=\s*["'](?:blob:|data:)/i.test(String(html || ''))
}

function createVideoUploadPipeline({
  fileBaseUrl,
  getDuration,
  transcode,
  requestSts,
  putFile,
  complete
}) {
  return async function uploadVideo(file, options = {}) {
    const onProgress = typeof options.onProgress === 'function' ? options.onProgress : () => {}

    if (!isSupportedVideoFile(file)) {
      throw new Error('The selected video format is not supported')
    }

    const duration = await getDuration(file)
    if (!isValidVideoDuration(duration)) {
      throw new Error('The video duration must be 120 seconds and below')
    }

    const uploadFile = await transcode(
      file,
      (progress) => onProgress(Math.round(Math.max(0, Math.min(100, progress)) * 0.5)),
      options
    )
    const stsData = await requestSts(uploadFile.name, options)
    const putResult = await putFile(
      uploadFile,
      stsData,
      (progress) => onProgress(Math.round(50 + Math.max(0, Math.min(100, progress)) * 0.5)),
      options
    )
    const completeResult = await complete(
      createCompletePayload(uploadFile, stsData.objectKey, putResult.etag),
      options
    )

    onProgress(100)

    return {
      src: resolvePublicMediaUrl(completeResult.filePath, fileBaseUrl),
      poster: completeResult.imagePath
        ? resolvePublicMediaUrl(completeResult.imagePath, fileBaseUrl)
        : '',
      fileName: completeResult.fileDisplayName || uploadFile.name
    }
  }
}

module.exports = {
  MAX_ANNOUNCEMENT_VIDEO_DURATION,
  MAX_ANNOUNCEMENT_VIDEO_TOTAL_SIZE,
  VIDEO_ACCEPT,
  VIDEO_EXTENSIONS,
  VIDEO_WIDTHS,
  buildOssCanonicalHeaders,
  buildOssStringToSign,
  buildOssUploadUrl,
  containsTemporaryVideoSource,
  createCompletePayload,
  createVideoUploadPipeline,
  isSupportedVideoFile,
  isValidVideoDuration,
  normalizeOssEtag,
  normalizeVideoWidth,
  resolvePublicMediaUrl
}
